<template>
  <div class="in-progress-tab">
    <!-- Loader -->
    <v-overlay :value="isLoading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-layout justify-space-between mb-3>
      <h2>Filtrer</h2>
      <v-spacer></v-spacer>
      <v-btn class="mr-6" @click="handlePrint">
        Imprimer l'édition en cours
      </v-btn>
      <v-btn @click="exportExcel">Export excel</v-btn>
    </v-layout>

    <!-- Filters Block -->
    <v-layout class="filters-container" mb-8 wrap>
      <v-flex sm12>
        <v-layout justify-space-between>
          <v-flex sm4>
            <v-dialog v-model="dialogDatepickerFrom" width="290px">
              <template #activator="{ on }">
                <v-text-field
                  :value="dateFrom | dateFormat"
                  label="Du"
                  append-icon="mdi-calendar"
                  v-on="on"
                  clearable
                  @click:clear="dateFrom = null"
                  single-line
                ></v-text-field>
              </template>
              <v-date-picker
                first-day-of-week="1"
                v-model="dateFrom"
                @change="dialogDatepickerFrom = false"
              >
              </v-date-picker>
            </v-dialog>
          </v-flex>
          <v-flex sm4>
            <v-dialog v-model="dialogDatepickerTo" width="290px">
              <template #activator="{ on }">
                <v-text-field
                  :value="dateTo | dateFormat"
                  label="Au"
                  append-icon="mdi-calendar"
                  v-on="on"
                  clearable
                  @click:clear="dateTo = null"
                  single-line
                ></v-text-field>
              </template>
              <v-date-picker
                first-day-of-week="1"
                v-model="dateTo"
                @change="dialogDatepickerTo = false"
              >
              </v-date-picker>
            </v-dialog>
          </v-flex>
          <v-flex sm4>
            <v-btn class="mt-4" block @click="onSearch">Rechercher</v-btn>
          </v-flex>
        </v-layout>
        <v-layout>
          <v-flex sm4>
            <v-select
              :items="dateTypes"
              label="Type de date"
              filled
              hide-details
              v-model="dateType"
            ></v-select>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>

    <v-data-table
      v-if="!isLoading"
      :headers="headers"
      :items="filteredProjects"
      group-by="applicator_fullname"
      fixed-header
      height="500"
      :items-per-page="-1"
      :footer-props="{
        itemsPerPageOptions: [-1, 100, 200, 300]
      }"
    >
      <!-- Date de moulage -->
      <template #item.created_at="{ value }">
        {{ value | dateFormat }}
      </template>

      <!-- Montant devis -->
      <template #item.total_amount_ht_lomaco_quotation="{ value }">
        {{ value | currency }}
      </template>

      <!-- Date de fabrication -->
      <template #item.cost_calculation_date="{ value }">
        {{ value | dateFormat }}
      </template>

      <!-- Montant fabrication -->
      <template #item.manufactured_cost="{ value }">
        {{ value | currency }}
      </template>

      <!-- Livré le -->
      <template #item.real_delivery_at="{ value }">
        {{ value | dateFormat }}
      </template>

      <template #group.header="{ group, items }">
        <td class="text-start" colspan="6">{{ group }}</td>
        <td class="text-start">
          Opération en cours pour applicateur : {{ items.length }}
        </td>
        <td class="text-start">
          Total montant devis :
          {{ calculateTotalQuotation(items) | currency }}
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
  import utils from "@/mixins/utils";

  import PrintRepo from "@/repositories/print";
  import ProjectsRepo from "@/repositories/projects";

  export default {
    name: "InProgressTab",
    mixins: [utils],
    data() {
      return {
        isLoading: false,

        // Datatable headers
        headers: [
          {
            text: "N° Dossier",
            align: "left",
            value: "file_number"
          },

          {
            text: "Date Moulage",
            align: "left",
            value: "created_at"
          },

          {
            text: "Nom du patient",
            align: "left",
            value: "patient_fullname"
          },

          {
            text: "Type",
            align: "left",
            value: "device_label"
          },

          {
            text: "Montant Devis",
            align: "left",
            value: "total_amount_ht_lomaco_quotation"
          },

          {
            text: "Date fabrication",
            align: "left",
            value: "cost_calculation_date"
          },

          {
            text: "Montant fabrication",
            align: "left",
            value: "manufactured_cost"
          },

          {
            text: "Livré le",
            align: "left",
            value: "real_delivery_at"
          }
        ],

        projects: [],

        // Search
        dateFrom: null,
        dialogDatepickerFrom: false,
        dateTo: null,
        dialogDatepickerTo: false,

        dateType: "OPERATIONS_IN_PROGRESS",

        dateTypes: [
          {
            value: "OPERATIONS_IN_PROGRESS",
            text: "Opérations en cours"
          }
        ]
      };
    },
    computed: {
      // Add props to project item
      filteredProjects() {
        return this.projects.map(item => {
          item.applicator_fullname = `${item.applicator_lastname.toUpperCase()} ${
            item.applicator_firstname
          }`;

          item.patient_fullname = `${item.patient_lastname} ${item.patient_firstname}`;

          return item;
        });
      }
    },
    methods: {
      /**
       * Download excel
       */
      async exportExcel() {
        const params = {
          dateFrom: this.dateFrom || null,
          dateTo: this.dateTo || new Date().toISOString().split("T")[0]
        };

        try {
          const res = await PrintRepo.getInProgressExcel(params);
          let blob = new Blob([res], {
            type: "application/vnd.ms-excel"
          });

          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);

          const date = new Date().toISOString().split("T")[0];
          const hour = new Date().toISOString().split("T")[1];
          const filename = `${date}-${hour}-encours.xlsx`;

          link.download = filename;
          link.click();
        } catch (error) {
          console.error(error);
        }
      },

      /**
       * Search projects by date range
       */
      async onSearch() {
        const options = {
          dateFrom: this.dateFrom || null,
          dateTo: this.dateTo || new Date().toISOString().split("T")[0]
        };

        const { data } = await ProjectsRepo.fetchInProgressProjects(options);
        this.projects = data;
      },

      /**
       * Sum prop value in an array of items
       *
       *  @param {Array} items
       */
      calculateTotalQuotation(items) {
        return items.reduce(
          (a, b) => a + (b.total_amount_ht_lomaco_quotation || 0),
          0
        );
      },

      // Print project and medias
      async handlePrint() {
        const params = {
          dateFrom: this.dateFrom || null,
          dateTo: this.dateTo || new Date().toISOString().split("T")[0]
        };

        try {
          const res = await PrintRepo.getInProgressPdf(params);
          let blob = new Blob([res], {
            type: "application/pdf"
          });

          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);

          const date = new Date().toISOString().split("T")[0];
          const hour = new Date().toISOString().split("T")[1];
          const filename = `${date}-${hour}-encours.pdf`;

          link.download = filename;
          link.click();
        } catch (error) {
          console.error(error);
        }
      }
    },
    async mounted() {
      this.isLoading = true;
      const { data } = await ProjectsRepo.fetchInProgressProjects();
      this.projects = data;
      this.isLoading = false;
    }
  };
</script>

<style lang="scss">
  .admin-tab {
    .v-data-table__wrapper {
      height: 470px !important;
    }
    .total {
      p {
        text-align: right;
        color: #006064;
        font-weight: bold;
        font-size: 15px !important;
      }
    }
  }
</style>
